import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";
import { defaultLang, languagesList } from "../i18n/strings";

interface LanguageState {
  language: string;
  setLanguage: (language: string) => void;
}

const isValidLanguage = (language?: string): language is string => {
  if (!language) {
    return false;
  }

  return languagesList.some((lang) => lang.key === language);
};

const getCookieValue = (name: string): string | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

  return cookieValue ? decodeURIComponent(cookieValue) : null;
};

export const getPersistedLanguageFromCookie = (): string | undefined => {
  const rawCookieValue = getCookieValue("lang");

  if (!rawCookieValue) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawCookieValue) as {
      state?: { language?: string };
    };
    const persistedLanguage = parsed?.state?.language;
    return isValidLanguage(persistedLanguage) ? persistedLanguage : undefined;
  } catch {
    return undefined;
  }
};

const cookieStorage: StateStorage = {
  getItem: (name) => getCookieValue(name),
  setItem: (name, value) => {
    if (typeof document === "undefined") {
      return;
    }

    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; samesite=lax`;
  },
  removeItem: (name) => {
    if (typeof document === "undefined") {
      return;
    }

    document.cookie = `${name}=; path=/; max-age=0; samesite=lax`;
  },
};

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set) => ({
      language: defaultLang,
      setLanguage: (language) => {
        if (!isValidLanguage(language)) {
          return;
        }

        set({ language });
      },
    }),
    {
      name: "lang",
      storage: createJSONStorage(() => cookieStorage),
      partialize: (state) => ({ language: state.language }),
    }
  )
);
